import React from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNACard, DNAText, Iffy, useTheme, DNAIcon, colors } from '@alucio/lux-ui';
import { DNAChip, DNACustomDeckThumbnail, DNAPopover, DNARowsComponent } from 'src/components/DNA';
import { FolderItemORM } from 'src/types/types';
import { isCustomDeckORM } from 'src/types/typeguards';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider.proxy';
export type DNADocumentFolderItemCustomDeckMeetingRowProps = {
  isOnline?: boolean,
}

const styles = StyleSheet.create({
  containerStyle: {
    backgroundColor: colors['color-white-transparent-6'],
    borderRadius: 4,
    marginVertical: 4,
  },
  titleText: {
    marginLeft: 24,
    paddingRight: 16,
  },
})

const DNADocumentFolderItemCustomDeckMeetingRow: DNARowsComponent<
  FolderItemORM,
  DNADocumentFolderItemCustomDeckMeetingRowProps
> = ({
  ORM: folderItem,
  onPress,
  style,
  isOnline,
}) => {
  const theme = useTheme()
  const { meetingORM } = useMeetingsState();

  /**
   * This is where typescript really helps us identify coding best practice. The way this and related components were structured/typed,
   * there is a possibility of getting into this component with the wrong ORM. This is a broader issue than just checking what type the related
   * item is. Basically, the folderItem comes in as any number of folderItemORM types and should be limited to just CustomDeckORM folderitem
   * types. Since this component is intended to only ever be loaded when we are using a customDeckORM, the data structure/typings should be identified
   * prior to loading the component. Then only the necessary props should be passed in.
   *
   * Note, that the folder item is being passed in and then passed right back out unnecessarily in the onPress call. Additionally, the DNACustomDeckContextMenu
   * component is being designed as a type specific component, but being passed a property that is multi-type. If the component is intended to be
   * specific to the datatype the props should reflect this approach as well. However, if this intended to be a reusable component, then passing the
   * more generic FolderItemORM is ok.
   * */
  if (!isCustomDeckORM(folderItem.relations.itemORM)) { return null }

  const customDeckORM = folderItem.relations.itemORM
  const isVirtualMeeting = meetingORM?.model.type === 'VIRTUAL'
  const hasWebDoc = customDeckORM.meta.containsWebDocs
  const hasHTMLDoc = customDeckORM.meta.containsHTMLDocs
  const { withinGracePeriod } = customDeckORM.meta.version
  const isReviewRequired = customDeckORM.meta.version.requiresReview
  const canPresent = customDeckORM.meta.permissions.MSLPresent

  const unavailableContent =
    (!isOnline && !customDeckORM.meta.assets.isContentCached) ||
    (hasWebDoc && (isVirtualMeeting || !isOnline)) ||
    (hasHTMLDoc && isVirtualMeeting) || !canPresent

  const isDisabledHTMLorWebDocument =
    (hasWebDoc && isVirtualMeeting) ||
    (hasHTMLDoc && isVirtualMeeting)

  const isInteractive =
    (!isReviewRequired || withinGracePeriod) &&
    !unavailableContent

  const handleItemPress = () => {
    isInteractive && onPress?.(folderItem)
  }

  const messages: string[] = [];
  if (isDisabledHTMLorWebDocument) {
    messages.push('Decks containing WEB or HTML file types are not able to be used in Presenter view');
  }
  if (!canPresent) {
    messages.push('This deck contains a slide that is not presentable.');
  }
  const popOverText = messages.join('\n');

  return (
    <DNACard
      appearance="flat"
      interactive={isInteractive ? 'pressable' : undefined}
      onPress={handleItemPress}
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
        styles.containerStyle,
      ]}
    >
      <DNAPopover disablePopover={!popOverText} placement="top-start">
        <DNAPopover.Anchor style={{ flex: 1 }}>
          <DNABox fill alignY="center" disabled={!isInteractive}>
            <DNABox fill alignY="center">
              <DNABox alignY="center">
                <DNACustomDeckThumbnail.Hover
                  popover={{
                    placement: 'left-start',
                  }}
                  customDeckORM={customDeckORM}
                  width={120}
                  height={68}
                  unavailableContent={unavailableContent}
                />
              </DNABox>
              <DNABox fill childFill={0} alignY="center" spacing="between">
                {/* Title */}
                <DNABox fill childFill appearance="col" spacing="sm" style={styles.titleText}>
                  <DNABox fill>
                    <DNAText b1 numberOfLines={1} colorMode="dark" testID="customDeck-item-title">
                      {folderItem.model.customTitle ? folderItem.model.customTitle : 'Untitled Presentation'}
                    </DNAText>
                  </DNABox>
                  <DNABox
                    alignY="center"
                    spacing="sm"
                    fill
                    childStyle={[4, { flex: 1 }]}
                  >
                    <DNAChip.Beacon variant="MODIFIED" />
                    <Iffy is={unavailableContent}>
                      <DNAChip.Beacon
                        testID={`content-not-available-${folderItem.meta.title}`}
                        variant="CONTENT_UNAVAILABLE"
                      />
                    </Iffy>
                  </DNABox>
                </DNABox>
                {/* Update Version Notification */}
                <Iffy is={isReviewRequired}>
                  <DNABox alignY="center" spacing="sm">
                    <DNAIcon.Styled name="warning" status="warning" />
                    <DNAText status="warning">
                      Needs review
                    </DNAText>
                  </DNABox>
                </Iffy>
              </DNABox>
            </DNABox>
          </DNABox>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText testID="web-html-custom-popup" colorMode="dark">
            { popOverText }
          </DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNACard>
  );
}

export default DNADocumentFolderItemCustomDeckMeetingRow
